import axiosLib from 'axios'
import { axios } from '@/api/axios.config'
import { JOB_STATUS_REQUEST_TIMEOUT_MS } from '@/api/caseImages'
import { v2Api } from '@/utils/global/api'
import type {
  CaseImageVariantAvailability,
  CaseImageVariantListItem,
  CaseImageVariantForCaseListItem,
  CaseImageVariantPricing,
  CaseImageVariantRevertedResponse,
  CreateCaseImageVariantInput,
  SetCaseImageVariantRevertedInput,
} from '@messenger/types'
import {
  CASE_IMAGE_JOB_STATUS_KEY,
  CASE_IMAGE_VARIANT_KIND_KEY,
} from '@messenger/consts'

/** The AI alteration kinds the backend can generate for a case image. */
export type CaseImageVariantKind = keyof typeof CASE_IMAGE_VARIANT_KIND_KEY

/**
 * Lifecycle of one generation attempt. It mirrors the case image job status
 * enum because a variant row doubles as its own job record.
 */
export type CaseImageVariantStatus = keyof typeof CASE_IMAGE_JOB_STATUS_KEY

/** Response of the status poll for one variant (one generation attempt). */
export type CaseImageVariantStatusResponse = {
  variantId: string
  caseImageId: string
  kind: CaseImageVariantKind
  status: CaseImageVariantStatus
  /** Where the generated image lives once the status is COMPLETED. */
  url: string | null
  /** True once the user chose to keep this result (the billable event). */
  accepted: boolean
  sourceVariantId: string | null
  /** Machine-readable failure reason when FAILED; the UI maps it to copy. */
  errorCode: string | null
}

/** Response of the process call: the variant to poll and its current status. */
export type ProcessCaseImageVariantResponse = {
  variantId: string
  status: CaseImageVariantStatus
}

/** Response of the accept call. */
export type AcceptCaseImageVariantResponse = {
  variantId: string
  accepted: boolean
}

const isNotFound = (err: unknown): boolean =>
  axiosLib.isAxiosError(err) && err.response?.status === 404

/**
 * Ask the backend to generate an alteration of a case image. The backend
 * reuses an identical in-flight generation instead of queueing a second one,
 * so calling this twice quickly is safe. A 429 means the daily cap is
 * reached; its JSON body carries the `code` the UI translates.
 */
export const processCaseImageVariant = async (
  input: CreateCaseImageVariantInput,
): Promise<ProcessCaseImageVariantResponse> => {
  const response = await axios.post<ProcessCaseImageVariantResponse>(
    `${v2Api}case-image-variants/process`,
    input,
  )
  return response.data
}

/**
 * Read the current status (and the output URL once complete) of a variant.
 * A poll that never answers must surface as a retryable timeout rather than
 * stall the waiting state forever, so each read gets the shared cap.
 */
export const getCaseImageVariantStatus = async (
  variantId: string,
): Promise<CaseImageVariantStatusResponse> => {
  const response = await axios.get<CaseImageVariantStatusResponse>(
    `${v2Api}case-image-variants/status/${variantId}`,
    { timeout: JOB_STATUS_REQUEST_TIMEOUT_MS },
  )
  return response.data
}

/**
 * Mark a completed variant as kept. This is the billable event, so it is only
 * called from an explicit user action.
 */
export const acceptCaseImageVariant = async (
  variantId: string,
): Promise<AcceptCaseImageVariantResponse> => {
  const response = await axios.post<AcceptCaseImageVariantResponse>(
    `${v2Api}case-image-variants/accept/${variantId}`,
  )
  return response.data
}

/**
 * Show the original photo instead of an accepted enhancement (`reverted`
 * true), or the enhancement again (false), for everyone: the backend saves
 * the choice on the variant, and every reader of the photo (the editor,
 * print, PDFs, m3) follows it. Nothing is billed either way.
 */
export const setCaseImageVariantReverted = async (
  variantId: string,
  reverted: boolean,
): Promise<CaseImageVariantRevertedResponse> => {
  const body: SetCaseImageVariantRevertedInput = { reverted }
  const response = await axios.put<CaseImageVariantRevertedResponse>(
    `${v2Api}case-image-variants/reverted/${variantId}`,
    body,
  )
  return response.data
}

/**
 * List every variant of one case image, newest first. The editor's Remove
 * Background tool uses it to offer a free re-apply of a result the user
 * already accepted. An unknown image is a real 404 and surfaces as one.
 */
export const getCaseImageVariantsForImage = async (
  caseImageId: string,
): Promise<CaseImageVariantListItem[]> => {
  const response = await axios.get<{ variants: CaseImageVariantListItem[] }>(
    `${v2Api}case-image-variants/for-image/${caseImageId}`,
    { timeout: JOB_STATUS_REQUEST_TIMEOUT_MS },
  )
  return response.data.variants ?? []
}

/**
 * List every variant of every image on a case, newest first, in one request.
 * Deploy-window fallback: a backend that does not serve this route yet
 * answers 404, which reads as "no variants" so the grid keeps working.
 * Remove the fallback once the route is live everywhere.
 */
export const getCaseImageVariantsForCase = async (
  caseId: string,
): Promise<CaseImageVariantForCaseListItem[]> => {
  try {
    const response = await axios.get<{
      variants: CaseImageVariantForCaseListItem[]
    }>(`${v2Api}case-image-variants/for-case/${caseId}`, {
      timeout: JOB_STATUS_REQUEST_TIMEOUT_MS,
    })
    return response.data.variants ?? []
  } catch (err) {
    if (isNotFound(err)) return []
    throw err
  }
}

/**
 * The price of accepting one variant of each kind, in cents. The UI shows
 * no amount until this has loaded; it never hardcodes a price.
 */
export const getCaseImageVariantPricing =
  async (): Promise<CaseImageVariantPricing> => {
    const response = await axios.get<CaseImageVariantPricing>(
      `${v2Api}case-image-variants/pricing`,
      { timeout: JOB_STATUS_REQUEST_TIMEOUT_MS },
    )
    return response.data
  }

/**
 * Whether AI image edits (background removal and Enhance) are switched on
 * for everyone. Read once per editor or dashboard load through
 * `useAiImageEditsEnabled`, which owns the answer; nothing else calls this.
 */
export const getCaseImageVariantAvailability =
  async (): Promise<CaseImageVariantAvailability> => {
    const response = await axios.get<CaseImageVariantAvailability>(
      `${v2Api}case-image-variants/availability`,
      { timeout: JOB_STATUS_REQUEST_TIMEOUT_MS },
    )
    return response.data
  }
